import { call, fork, put, take, select } from 'redux-saga/effects';
import { debug, error } from '../utils/logger';
import { isAuthorized } from '../utils/tokenUtils';

import {
    FETCH_HEARTED_PICS,
    HEART_PIC,
    UNHEART_PIC,
    fetchHeartedPics,
    fetchHeartedPicsSuccess,
    fetchHeartedPicsError,
    heartPicSuccess,
    heartPicError,
    unheartPicSuccess,
    unheartPicError
} from '../actions/engageActions';
import { heartPicTile, unheartPicTile } from '../utils/heartsUtils';
import { isFavoritesEnabled } from '../utils/favoritesUtils';
import { LOGIN_SUCCESS } from '../actions/userActions';
import { handleUserLogin } from '../sagas/loginSagas';
import { handleUserNameRequiredSaga } from '../sagas/userSagas';

import { makeRequest } from './makeRequest';

import { getHeartedPicsRoute, heartPicRoute, unheartPicRoute } from '../utils/apiRoutes';

export function *fetchHeartedPicsSaga() {
    try {
        const state = yield select();
        const heartedPics = yield call(makeRequest, getHeartedPicsRoute(state));

        yield put(fetchHeartedPicsSuccess(heartedPics));
    } catch(err) {
        error(`Unable to get hearted pics: ${err}`);

        if (err.status && err.status.code) {
            const errorServerResponse = err;

            if (404 === errorServerResponse.status.code) {
                yield put(fetchHeratedPicsSuccess({}));
            } else {
                yield put(fetchHeartedPicsError(errorServerResponse));
            }
        } else {
            yield put(fetchHeartedPicsError(err));
        }
    }
}

/**
 * Heart a picture. Call the service to do the work
 * @param id Id of the pic from Engage
 * @param component Component that fired the originating action
 */
export function *heartPicSaga(id, component) {
    try {
        const state = yield select();
        yield call(makeRequest, heartPicRoute(state, id));
        yield call(heartPicTile, id);
        yield put(heartPicSuccess(id, component));
    } catch (err) {
        yield put(heartPicError(err));
    }
}

/**
 * Unheart a picture. Call the service to do the work
 * @param id Id of the pic from Engage
 * @param component Component that fired the originating action
 */
export function *unheartPicSaga(id, component) {
    try {
        const state = yield select();
        yield call(makeRequest, unheartPicRoute(state, id));
        yield call(unheartPicTile, id);
        yield put(unheartPicSuccess(id, component));
    } catch (err) {
        yield put(unheartPicError(err));
    }
}

/**
 * Fetch Hearted Pics.  This should be fired from the UGC Component
 *  when an Engage datasource is detected
 */
export function *fetchHeartedPicsListenerSaga() {
    debug("Saga: fetchHeartedPicsSaga initialized");

    if (!isFavoritesEnabled()) {
        debug("Engage not enabled. Exiting");
        return;
    }

    while (true) {
        yield take(FETCH_HEARTED_PICS);

        // if the user is not logged in don't fetch the hearted pics
        const authorized = yield call(isAuthorized);
        if (authorized) {
            yield call(fetchHeartedPicsSaga);
        } else {
            yield put(fetchHeartedPicsSuccess({}));
        }
    }
}

/**
 * User clicks the heart on a pic
 */
export function *heartPicListenerSaga() {
    debug("Saga: heartPicSaga initialized");

    if (!isFavoritesEnabled()) {
        debug("Engage not enabled. Exiting");
        return;
    }

    let action = undefined;
    while (action = yield take(HEART_PIC)) {
        if (action && action.payload) {
            const id = _.get(action, 'payload.id', '');
            const component = _.get(action, 'meta.component', {});

            const effectToRun = fork(heartPicSaga, id, component);
            yield call(ensureAuthorized, effectToRun);
        }
    }
}

/**
 * Wrapping saga to ensure that:
 *   - the user is authorized and token is valid.  If not, launch the login modal and wait
 *   - the user has a profile and a username, if not:
 *      - abort the flow
 *      - launch the username modal
 *      - if the username modal creates a username in the profile, run the saga effect
 *
 * @param sagaEffectToRun saga effect to run if authorized conditions pass
 */
export function *ensureAuthorized(sagaEffectToRun) {
    debug("ensureAuthorized: start");

    // Ensure authorized
    const loginState = yield call(handleUserLogin);
    if (!loginState.loginFinished) {
        debug("Login flow canceled");
        return false;
    }

    const userNameState = yield call(handleUserNameRequiredSaga);
    if (!userNameState.finished) {
        debug("Username flow canceled");
        return false;
    } else {
        yield sagaEffectToRun;
    }
}

/**
 * User clicks the unheart
 */
export function *unheartPicListenerSaga() {
    debug("Saga: unheartPicSaga initialized");

    if (!isFavoritesEnabled()) {
        debug("Engage not enabled. Exiting");
        return;
    }

    let action = undefined;
    while (action = yield take(UNHEART_PIC)) {
        if (action && action.payload) {
            const id = _.get(action, 'payload.id', '');
            const component = _.get(action, 'meta.component', {});

            yield fork(unheartPicSaga, id, component);
        }
    }
}

/**
 * Hearts should be fetched when a user successfully logs in
 */
export function *heartsRefreshUserLoginListenerSaga() {
    debug("Saga: favoritesRefreshUserLoginSaga initialized");

    if (!isFavoritesEnabled()) {
        debug("Engage not enabled. Exiting");
        return;
    }

    while (true) {
        yield take(LOGIN_SUCCESS);
        yield put(fetchHeartedPics());
    }
}

export default [
    heartsRefreshUserLoginListenerSaga,
    fetchHeartedPicsListenerSaga,
    heartPicListenerSaga,
    unheartPicListenerSaga
];
